import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  LogOut, BarChart3, Mail, Users, FileText, Download, 
  Eye, RefreshCw, ChevronDown, Lock, BookOpen, Globe,
  TrendingUp, Zap, ArrowDownToLine, Award, CheckCircle2, Clock, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4"><Lock size={24} className="text-white" /></div>
          <h1 className="text-2xl font-bold text-slate-900">Documatch Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Dashboard de administración</p>
        </div>
        {error && <p className="text-red-500 text-sm text-center mb-4 bg-red-50 p-2 rounded" data-testid="login-error">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required data-testid="admin-email" placeholder="admin@documatch.eu" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <Input value={password} onChange={e => setPassword(e.target.value)} type="password" required data-testid="admin-password" /></div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="admin-login-btn">{loading ? "..." : "Acceder"}</Button>
        </form>
      </motion.div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon size={20} className="text-white" /></div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function SimpleBar({ data, title, colorClass = "bg-blue-500" }) {
  if (!data || Object.keys(data).length === 0) return null;
  const max = Math.max(...Object.values(data), 1);
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
      <h3 className="font-semibold text-slate-800 text-sm mb-4">{title}</h3>
      <div className="space-y-3">
        {Object.entries(data).sort((a, b) => b[1] - a[1]).map(([key, val]) => (
          <div key={key}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600 capitalize">{key}</span>
              <span className="font-medium text-slate-800">{val}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${colorClass} rounded-full transition-all`} style={{ width: `${(val / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DataTable({ title, data, columns, onExport }) {
  const [expanded, setExpanded] = useState(true);
  if (!data || data.length === 0) return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
      <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
      <p className="text-sm text-slate-400">No data yet</p>
    </div>
  );
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 hover:text-blue-600">
          <h3 className="font-semibold text-slate-800">{title} ({data.length})</h3>
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
        {onExport && <button onClick={onExport} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`export-${title.toLowerCase().replace(/\s/g,'-')}`}>
          <ArrowDownToLine size={12} /> CSV
        </button>}
      </div>
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-t border-slate-100">
              <tr>{columns.map(col => <th key={col.key} className="text-left px-4 py-2 text-xs font-medium text-slate-500 uppercase">{col.label}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.slice(0, 25).map((row, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  {columns.map(col => (
                    <td key={col.key} className="px-4 py-2.5 text-slate-700 max-w-[200px] truncate">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] || "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [demos, setDemos] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [sponsorRequests, setSponsorRequests] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = searchParams.get("tab") || "overview";
  const validTabs = ["overview", "demos", "leads", "sponsors", "events", "analytics"];
  const [tab, setTab] = useState(validTabs.includes(initialTab) ? initialTab : "overview");

  // SEO: prevent indexing of admin page
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Documatch · Admin";
    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    const prevContent = robots.getAttribute("content");
    robots.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => {
      document.title = prevTitle;
      if (created) {
        document.head.removeChild(robots);
      } else if (prevContent !== null) {
        robots.setAttribute("content", prevContent);
      }
    };
  }, []);

  // Sync tab → URL (?tab=sponsors)
  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === "overview") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", newTab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const token = localStorage.getItem("admin_token");
  // Read token fresh on each call to avoid stale-closure bug after login
  const auth = () => {
    const t = localStorage.getItem("admin_token");
    return { headers: { Authorization: `Bearer ${t}` }, withCredentials: true };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [s, an, d, c, l, ev, sr] = await Promise.all([
        axios.get(`${API}/admin/stats-overview`, auth()),
        axios.get(`${API}/admin/analytics`, auth()),
        axios.get(`${API}/admin/demo-requests`, auth()),
        axios.get(`${API}/admin/contacts`, auth()),
        axios.get(`${API}/admin/leads`, auth()),
        axios.get(`${API}/admin/events`, auth()),
        axios.get(`${API}/admin/sponsor-requests`, auth()),
      ]);
      setStats(s.data); setAnalytics(an.data); setDemos(d.data.requests);
      setContacts(c.data.requests); setLeads(l.data.leads); setEvents(ev.data.events);
      setSponsorRequests(sr.data.items || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("admin_token"); setUser(null);
      }
    } finally { setLoading(false); }
  };

  const approveSponsorRequest = async (requestId) => {
    setApprovingId(requestId);
    try {
      const { data } = await axios.post(
        `${API}/admin/sponsor-approve`,
        { request_id: requestId },
        auth()
      );
      // Refresh list
      const sr = await axios.get(`${API}/admin/sponsor-requests`, auth());
      setSponsorRequests(sr.data.items || []);
      alert(`✓ Email d'approbation envoyé à ${data.email_sent_to}\n\nLien privé : ${data.private_url}`);
    } catch (err) {
      alert(`Erreur : ${err.response?.data?.detail || err.message}`);
    } finally {
      setApprovingId(null);
    }
  };

  useEffect(() => {
    if (token && !user) {
      axios.get(`${API}/auth/me`, auth())
        .then(res => { setUser(res.data); fetchData(); })
        .catch(() => { localStorage.removeItem("admin_token"); setLoading(false); });
    } else { setLoading(false); }
  }, [token]);

  const handleExport = (collection) => {
    window.open(`${API}/admin/export/${collection}?token=${token}`, "_blank");
  };

  if (!token || !user) return <AdminLogin onLogin={(data) => { setUser(data); fetchData(); }} />;

  const demoColumns = [
    { key: "created_at", label: "Fecha", render: v => v ? new Date(v).toLocaleDateString() : "-" },
    { key: "name", label: "Nombre", render: (v, r) => v || `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-" },
    { key: "email", label: "Email" },
    { key: "company", label: "Empresa" },
    { key: "solution", label: "Solución" },
    { key: "language", label: "Idioma" }
  ];
  const contactColumns = [
    { key: "created_at", label: "Fecha", render: v => v ? new Date(v).toLocaleDateString() : "-" },
    { key: "name", label: "Nombre" }, { key: "email", label: "Email" },
    { key: "message", label: "Mensaje" }
  ];
  const leadColumns = [
    { key: "created_at", label: "Fecha", render: v => v ? new Date(v).toLocaleDateString() : "-" },
    { key: "email", label: "Email" }, { key: "company", label: "Empresa" },
    { key: "language", label: "Idioma" }
  ];
  const eventColumns = [
    { key: "created_at", label: "Fecha", render: v => v ? new Date(v).toLocaleString() : "-" },
    { key: "event", label: "Evento" }, { key: "source", label: "Fuente" },
    { key: "language", label: "Idioma" }, { key: "solution", label: "Solución" }
  ];

  const tabs = [
    { id: "overview", label: "Resumen", icon: BarChart3 },
    { id: "demos", label: "Demos", icon: FileText },
    { id: "leads", label: "Leads", icon: Download },
    { id: "sponsors", label: "Sponsors", icon: Award },
    { id: "events", label: "Eventos", icon: Zap },
    { id: "analytics", label: "Analytics", icon: TrendingUp }
  ];

  const pendingSponsorCount = sponsorRequests.filter(r => r.status === "pending_validation").length;

  const totalLeads = (stats?.demos || 0) + (stats?.contacts || 0) + (stats?.leads || 0);
  const ebookDownloads = analytics?.ebook_downloads || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 size={20} className="text-blue-600" />
            <span className="font-bold text-slate-900">Documatch Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchData} data-testid="admin-refresh"><RefreshCw size={14} /></Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}><Eye size={14} /></Button>
            <Button variant="outline" size="sm" onClick={async () => {
              try { await axios.post(`${API}/auth/logout`, {}, auth()); } catch {}
              localStorage.removeItem("admin_token");
              setUser(null);
            }} data-testid="admin-logout"><LogOut size={14} /></Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto pb-0.5">
          {tabs.map(t => (
            <button key={t.id} onClick={() => handleTabChange(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${tab === t.id ? "bg-blue-50 text-blue-600 border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon size={14} />{t.label}
              {t.id === "sponsors" && pendingSponsorCount > 0 && (
                <span className="ml-1 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="sponsor-pending-badge">
                  {pendingSponsorCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Tab */}
        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatCard icon={Users} label="Total Leads" value={totalLeads} color="bg-blue-600" />
              <StatCard icon={FileText} label="Solicitudes Demo" value={stats?.demos || 0} color="bg-indigo-600" />
              <StatCard icon={BookOpen} label="Descargas eBook" value={ebookDownloads} color="bg-purple-600" />
              <StatCard icon={Mail} label="Contactos" value={stats?.contacts || 0} color="bg-green-600" />
              <StatCard icon={Eye} label="Visitantes" value={stats?.visitors || 0} color="bg-orange-500" />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <SimpleBar data={analytics?.demos_by_solution} title="Demos por Solución" colorClass="bg-blue-500" />
              <SimpleBar data={analytics?.demos_by_language} title="Demos por Idioma" colorClass="bg-green-500" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SimpleBar data={analytics?.leads_by_language} title="Leads eBook por Idioma" colorClass="bg-purple-500" />
              <SimpleBar data={analytics?.event_counts} title="Eventos por Tipo" colorClass="bg-orange-500" />
            </div>

            {/* GA4 Future Integration Notice */}
            <div className="bg-slate-100 rounded-xl p-4 border border-dashed border-slate-300 text-center">
              <Globe size={20} className="text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Fuentes de tráfico, comportamiento usuario y alertas disponibles cuando se integre GA4 API</p>
            </div>
          </>
        )}

        {/* Demos Tab */}
        {tab === "demos" && (
          <DataTable title="Solicitudes de Demo" data={demos} columns={demoColumns} onExport={() => handleExport("demos")} />
        )}

        {/* Leads Tab */}
        {tab === "leads" && (
          <>
            <DataTable title="Leads eBook" data={leads} columns={leadColumns} onExport={() => handleExport("leads")} />
            <DataTable title="Contactos" data={contacts} columns={contactColumns} onExport={() => handleExport("contacts")} />
          </>
        )}

        {/* Sponsors Tab */}
        {tab === "sponsors" && (
          <div className="space-y-4" data-testid="sponsors-tab-content">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <Award size={18} className="text-blue-600" />
                    Demandes Sponsor
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">Validation manuelle des partenaires · Approbation envoie un lien privé sécurisé</p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded-md font-medium">
                    <Clock size={11} /> {pendingSponsorCount} en attente
                  </span>
                  <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-md font-medium">
                    <CheckCircle2 size={11} /> {sponsorRequests.filter(r => r.status === "approved").length} approuvés
                  </span>
                </div>
              </div>

              {sponsorRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Award size={32} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Aucune demande sponsor pour le moment</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-5">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-y border-slate-100">
                      <tr>
                        <th className="text-left px-5 py-2 text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Société</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Contact</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Email</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">ERP/GED</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Lang</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Statut</th>
                        <th className="text-right px-5 py-2 text-xs font-medium text-slate-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sponsorRequests.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50" data-testid={`sponsor-request-row-${r.id}`}>
                          <td className="px-5 py-2.5 text-slate-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "-"}</td>
                          <td className="px-3 py-2.5 font-medium text-slate-900">{r.company || "-"}</td>
                          <td className="px-3 py-2.5 text-slate-700">{r.fullName || "-"}</td>
                          <td className="px-3 py-2.5 text-slate-700 max-w-[180px] truncate">
                            <a href={`mailto:${r.email}`} className="text-blue-600 hover:underline">{r.email}</a>
                          </td>
                          <td className="px-3 py-2.5 text-slate-500 max-w-[140px] truncate">{r.erp || "-"}</td>
                          <td className="px-3 py-2.5 text-slate-500 uppercase text-xs">{r.language || "fr"}</td>
                          <td className="px-3 py-2.5">
                            {r.status === "approved" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-[11px] font-medium">
                                <CheckCircle2 size={11} /> Approuvé
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[11px] font-medium">
                                <Clock size={11} /> En attente
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            {r.status === "approved" ? (
                              <span className="text-xs text-slate-400">Email envoyé</span>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => approveSponsorRequest(r.id)}
                                disabled={approvingId === r.id}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3"
                                data-testid={`approve-sponsor-${r.id}`}
                              >
                                {approvingId === r.id ? (
                                  <><Loader2 size={11} className="animate-spin mr-1" />…</>
                                ) : (
                                  <>Approuver</>
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Events Tab */}
        {tab === "events" && (
          <DataTable title="Eventos Tracked" data={events} columns={eventColumns} onExport={() => handleExport("events")} />
        )}

        {/* Analytics Tab */}
        {tab === "analytics" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={BookOpen} label="eBook Downloads" value={ebookDownloads} color="bg-purple-600" />
              <StatCard icon={Zap} label="CTA Clicks" value={analytics?.event_counts?.cta_click || 0} color="bg-yellow-500" />
              <StatCard icon={FileText} label="Form Submits" value={analytics?.event_counts?.form_submit || 0} color="bg-green-600" />
              <StatCard icon={Eye} label="Landing Views" value={analytics?.event_counts?.landing_view || 0} color="bg-blue-500" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SimpleBar data={analytics?.demos_by_solution} title="Rendimiento por Solución GED" colorClass="bg-indigo-500" />
              <SimpleBar data={analytics?.demos_by_language} title="Rendimiento por Idioma" colorClass="bg-teal-500" />
            </div>
            <div className="bg-slate-100 rounded-xl p-4 border border-dashed border-slate-300 text-center">
              <TrendingUp size={20} className="text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Funnel de conversión detallado, CPL y tasa de conversión disponibles con integración GA4 API</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
